import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  User, 
  Phone, 
  MapPin, 
  CreditCard, 
  Banknote,
  Loader2,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Layout } from '../components/Layout';
import { useCart, optionsLabel } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickCheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(null);

  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    delivery_street: '',
    delivery_postal_code: '',
    delivery_city: '',
    delivery_additional_info: '',
    payment_method: 'cash',
    desired_time: '',
    special_instructions: '',
  });

  const [deliveryZones, setDeliveryZones] = useState([]);

  useEffect(() => {
    axios.get(`${API}/delivery/zones`).then(res => setDeliveryZones(res.data));
  }, []);

  const communeOptions = deliveryZones
    .flatMap(z => z.communes || z.postal_codes.map(cp => ({ postal_code: cp, city: z.name })))
    .sort((a, b) => a.city.localeCompare(b.city, 'fr'));

  const handleCommuneChange = (value) => {
    const [postal_code, city] = value.split('|');
    setForm(f => ({ ...f, delivery_postal_code: postal_code, delivery_city: city }));
  };

  const zone = deliveryZones.find(z => z.postal_codes.includes(form.delivery_postal_code));
  const isFreeDelivery = zone?.free_delivery_threshold && cart.total >= zone.free_delivery_threshold;
  const deliveryFee = isFreeDelivery ? 0 : (zone?.delivery_fee || 0);
  const total = cart.total + deliveryFee;
  const minOrderNotMet = zone && cart.total < zone.min_order;

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return form.customer_name.trim() &&
           form.customer_phone.trim() &&
           form.delivery_street.trim() &&
           form.delivery_postal_code &&
           form.delivery_city.trim() &&
           cart.items.length > 0 &&
           zone &&
           !minOrderNotMet;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(`${API}/orders/quick`, {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        delivery_street: form.delivery_street,
        delivery_postal_code: form.delivery_postal_code,
        delivery_city: form.delivery_city,
        delivery_additional_info: form.delivery_additional_info || null,
        items: cart.items.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity,
          special_instructions: item.special_instructions || null,
          add_shrimp: !!item.options?.add_shrimp,
          kids_portion: !!item.options?.kids_portion,
          formule_drink_id: item.options?.formule?.drink_id || null,
          formule_dessert_id: item.options?.formule?.dessert_id || null,
        })),
        payment_method: form.payment_method,
        desired_time: form.desired_time || null,
        special_instructions: form.special_instructions || null,
      });

      setOrderPlaced(response.data);
      await clearCart();
      toast.success('Commande confirmée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setLoading(false);
    }
  };

  // Order confirmation screen
  if (orderPlaced) {
    return (
      <Layout showFooter={false}>
        <div className="max-w-2xl mx-auto px-4 py-12 md:py-20" data-testid="order-confirmed">
          <div className="text-center mb-12">
            <div className="w-24 h-24 mx-auto bg-accent/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-12 w-12 text-accent" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Commande confirmée !
            </h1>
            <p className="text-muted-foreground text-lg">
              Merci pour votre commande, {form.customer_name} 🎉
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm space-y-6">
            <div>
              <p className="text-sm text-muted-foreground">Numéro de commande</p>
              <p className="font-mono text-lg font-semibold">{orderPlaced.order_id}</p>
            </div>

            <div className="border-t border-border pt-6">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Sous-total</span>
                <span>{orderPlaced.subtotal.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Livraison</span>
                <span>
                  {orderPlaced.is_free_delivery ? (
                    <span className="text-accent font-semibold">OFFERTE</span>
                  ) : (
                    `${orderPlaced.delivery_fee.toFixed(2)} €`
                  )}
                </span>
              </div>
              <div className="flex justify-between pt-3 border-t border-border font-semibold text-lg">
                <span>Total</span>
                <span className="text-primary">{orderPlaced.total.toFixed(2)} €</span>
              </div>
            </div>

            <div className="bg-primary/5 rounded-xl p-4 text-center">
              <p className="text-sm">
                <span className="text-muted-foreground">Livraison estimée : </span>
                <span className="font-semibold text-primary">{orderPlaced.estimated_delivery}</span>
              </p>
              <p className="text-sm mt-2">
                <span className="text-muted-foreground">Paiement : </span>
                <span className="font-medium">
                  {orderPlaced.payment_method === 'cash' ? '💵 Espèces à la livraison' : '💳 Carte à la livraison'}
                </span>
              </p>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Nous vous appellerons au <strong>{form.customer_phone}</strong> si besoin.
            </p>
          </div>

          <div className="flex justify-center gap-4 mt-8">
            <Button 
              onClick={() => navigate('/menu')} 
              variant="outline" 
              className="rounded-full"
              data-testid="back-to-menu-btn"
            >
              Retour à la carte
            </Button>
            <Button 
              onClick={() => navigate('/')} 
              className="rounded-full"
              data-testid="back-home-btn"
            >
              Accueil
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Empty cart
  if (cart.items.length === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-serif font-bold mb-4">Votre panier est vide</h1>
          <p className="text-muted-foreground mb-6">
            Ajoutez des plats avant de passer commande.
          </p>
          <Button onClick={() => navigate('/menu')} className="rounded-full" data-testid="browse-menu-btn">
            Voir la carte
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12" data-testid="quick-checkout-page">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/cart')} 
          className="mb-4 rounded-full"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour au panier
        </Button>

        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-8">
          Finaliser ma commande
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="contact-section">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-primary" />
                Vos informations
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nom complet *</Label>
                  <Input
                    placeholder="Jean Dupont"
                    value={form.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label>Téléphone *</Label>
                  <Input
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={form.customer_phone}
                    onChange={(e) => handleChange('customer_phone', e.target.value)}
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="delivery-section">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-primary" />
                Adresse de livraison
              </h2>
              <div className="space-y-4">
                <div>
                  <Label>Adresse *</Label>
                  <Input
                    placeholder="Numéro et rue"
                    value={form.delivery_street}
                    onChange={(e) => handleChange('delivery_street', e.target.value)}
                    data-testid="input-street"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Commune / Code postal *</Label>
                    <Select
                      value={form.delivery_postal_code ? `${form.delivery_postal_code}|${form.delivery_city}` : ''}
                      onValueChange={handleCommuneChange}
                    >
                      <SelectTrigger data-testid="select-postal-code">
                        <SelectValue placeholder="Choisir votre commune" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {communeOptions.map(c => (
                          <SelectItem
                            key={`${c.postal_code}|${c.city}`}
                            value={`${c.postal_code}|${c.city}`}
                            data-testid={`commune-option-${c.postal_code}-${c.city}`}
                          >
                            {c.city} ({c.postal_code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Ville *</Label>
                    <Input
                      placeholder="Ville"
                      value={form.delivery_city}
                      onChange={(e) => handleChange('delivery_city', e.target.value)}
                      data-testid="input-city"
                    />
                  </div>
                </div>
                <div>
                  <Label>Informations complémentaires</Label>
                  <Input
                    placeholder="Étage, bâtiment, code d'accès..."
                    value={form.delivery_additional_info}
                    onChange={(e) => handleChange('delivery_additional_info', e.target.value)}
                    data-testid="input-additional-info"
                  />
                </div>

                {zone && (
                  <div className="p-4 bg-accent/10 rounded-xl text-sm">
                    <p className="font-medium text-accent">
                      ✓ Zone {zone.name} - Livraison en {zone.estimated_time}
                    </p>
                    {isFreeDelivery && (
                      <p className="text-accent font-semibold mt-1">
                        🎉 Livraison OFFERTE !
                      </p>
                    )}
                    {minOrderNotMet && (
                      <p className="text-destructive mt-1">
                        ⚠ Commande minimum : {zone.min_order}€ (il vous manque {(zone.min_order - cart.total).toFixed(2)}€)
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="payment-section">
              <h2 className="text-xl font-semibold mb-4">Heure de livraison souhaitée</h2>
              <div className="mb-6">
                <Label>À quelle heure souhaitez-vous être livré ?</Label>
                <Input
                  type="time"
                  value={form.desired_time}
                  onChange={(e) => handleChange('desired_time', e.target.value)}
                  className="mt-1"
                  data-testid="input-desired-time"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Horaires de livraison : Jeu-Ven 19h-22h • Sam-Dim 11h-14h & 18h-22h
                </p>
              </div>

              <h2 className="text-xl font-semibold mb-4">Mode de paiement</h2>
              <RadioGroup
                value={form.payment_method}
                onValueChange={(value) => handleChange('payment_method', value)}
                className="space-y-3"
              >
                <div className={`flex items-center space-x-3 p-4 rounded-xl border ${
                  form.payment_method === 'cash' ? 'border-primary bg-primary/5' : 'border-border'
                }`}>
                  <RadioGroupItem value="cash" id="cash" data-testid="payment-cash" />
                  <Label htmlFor="cash" className="flex items-center cursor-pointer flex-1">
                    <Banknote className="h-5 w-5 mr-3 text-accent" />
                    <div>
                      <p className="font-medium">Espèces à la livraison</p>
                      <p className="text-sm text-muted-foreground">Paiement en cash au livreur</p>
                    </div>
                  </Label>
                </div>

                <div className={`flex items-center space-x-3 p-4 rounded-xl border ${
                  form.payment_method === 'card_on_delivery' ? 'border-primary bg-primary/5' : 'border-border'
                }`}>
                  <RadioGroupItem value="card_on_delivery" id="card_on_delivery" data-testid="payment-card" />
                  <Label htmlFor="card_on_delivery" className="flex items-center cursor-pointer flex-1">
                    <CreditCard className="h-5 w-5 mr-3 text-primary" />
                    <div>
                      <p className="font-medium">Carte à la livraison</p>
                      <p className="text-sm text-muted-foreground">Paiement par carte auprès du livreur</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Special Instructions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4">Instructions spéciales</h2>
              <Textarea
                placeholder="Allergies, préférences, instructions pour le livreur..."
                value={form.special_instructions}
                onChange={(e) => handleChange('special_instructions', e.target.value)}
                className="min-h-[100px]"
                data-testid="input-instructions"
              />
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-muted rounded-2xl p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-serif font-semibold mb-6">Récapitulatif</h2>

              <div className="space-y-3 mb-6 max-h-64 overflow-y-auto pr-2">
                {cart.items.map((item) => {
                  const lbl = optionsLabel(item.options);
                  return (
                    <div key={`${item.item_id}__${JSON.stringify(item.options || {})}`} className="text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground line-clamp-1 pr-2">
                          {item.quantity}x {item.name}
                        </span>
                        <span className="font-medium whitespace-nowrap">{item.subtotal.toFixed(2)} €</span>
                      </div>
                      {lbl && <p className="text-xs text-accent mt-0.5">↳ {lbl}</p>}
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-border pt-4 space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span>{cart.total.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Livraison</span>
                  <span>
                    {isFreeDelivery ? (
                      <span className="text-accent font-medium">OFFERTE</span>
                    ) : deliveryFee > 0 ? (
                      `${deliveryFee.toFixed(2)} €`
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
              </div>

              <div className="border-t border-border pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-primary text-2xl">
                    {total.toFixed(2)} €
                  </span>
                </div>
              </div>

              <Button
                className="w-full rounded-full bg-primary hover:bg-primary/90 h-12"
                size="lg"
                onClick={handleSubmit}
                disabled={loading || !isFormValid()}
                data-testid="confirm-order-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Confirmer ma commande'
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                En confirmant, vous acceptez d&apos;être contacté par téléphone pour la livraison.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default QuickCheckoutPage;
